import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { recent, getStats, startActivity, getMe } from "../lib/api";

const API_BASE = process.env.REACT_APP_API_BASE ?? "/api";

function buildPfpSrc(url, token) {
  if (!url) return null;
  const full = url.startsWith("http") ? url : `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
  const join = full.includes("?") ? "&" : "?";
  return `${full}${join}t=${encodeURIComponent(token)}`;
}

function rankIndex(username, list) {
  if (!username || !Array.isArray(list)) return -1;
  return list.findIndex(r => r.username === username);
}

function medalColor(idx) {
  if (idx === 0) return "#eab308";
  if (idx === 1) return "#9ca3af";
  if (idx === 2) return "#92400e";
  return null;
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  const rad = Math.PI / 180;
  const s = startDeg * rad;
  const e = endDeg * rad;
  const x1 = cx + r * Math.cos(s);
  const y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e);
  const y2 = cy + r * Math.sin(e);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

function MedalRing({ activeColors }) {
  const size = 28;
  const cx = 14;
  const cy = 14;
  const r = 13;
  const sw = 2;
  const seg = 68;
  const starts = [-115, -25, 65, 155];
  const arcs = starts.map((a, i) => ({
    d: arcPath(cx, cy, r, a, a + seg),
    color: activeColors[i]
  }));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 pointer-events-none">
      {arcs.map((p, i) => (
        <path key={i} d={p.d} stroke={p.color || "transparent"} strokeWidth={sw} strokeLinecap="round" fill="none" />
      ))}
    </svg>
  );
}

function TinyStars({ colors }) {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <style>{`@keyframes pop{0%{transform:scale(0.6);opacity:0}25%{opacity:1}70%{opacity:1}100%{transform:scale(1.05);opacity:0}}`}</style>
      {colors[0] ? <span style={{position:"absolute",top:"-6px",left:"11px",width:"3px",height:"3px",background:colors[0],borderRadius:"9999px",animation:"pop 1.8s ease-in-out infinite"}}/> : null}
      {colors[1] ? <span style={{position:"absolute",top:"5px",right:"-7px",width:"3px",height:"3px",background:colors[1],borderRadius:"9999px",animation:"pop 1.8s ease-in-out infinite 0.3s"}}/> : null}
      {colors[2] ? <span style={{position:"absolute",bottom:"-6px",left:"10px",width:"3px",height:"3px",background:colors[2],borderRadius:"9999px",animation:"pop 1.8s ease-in-out infinite 0.6s"}}/> : null}
      {colors[3] ? <span style={{position:"absolute",bottom:"5px",right:"-7px",width:"3px",height:"3px",background:colors[3],borderRadius:"9999px",animation:"pop 1.8s ease-in-out infinite 0.9s"}}/> : null}
    </div>
  );
}

function formatDDMMYY(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function fmtHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function Item({ a, token, stats }) {
  const dStart = new Date(a.startedAt);
  const dEnd = a.endedAt ? new Date(a.endedAt) : null;

  const dateStr = formatDDMMYY(dStart);
  const startTime = dStart.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const endTime = dEnd ? dEnd.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : null;

  const ongoing = a.ongoing === true;
  const status = ongoing
    ? { text: "Ongoing", chip: "bg-green-100 text-green-800", dot: "bg-green-500" }
    : { text: "Finished", chip: "bg-red-100 text-red-800", dot: "bg-red-500" };

  const pfpSrc = buildPfpSrc(a.pfpUrl, token);

  const idxSquares = rankIndex(a.username, stats?.topCuadrados);
  const idxTime = rankIndex(a.username, stats?.topTiempo);
  const idxMinCps = rankIndex(a.username, stats?.topMinCps);
  const idxMaxSession = rankIndex(a.username, stats?.topMaxSession);
  const colors = [
    medalColor(idxSquares),
    medalColor(idxTime),
    medalColor(idxMinCps),
    medalColor(idxMaxSession)
  ];

  return (
    <div className="relative w-full h-full border border-neutral-200 rounded-2xl px-5 py-4 bg-white shadow-sm">
      <style>{`
        @keyframes pulse2s{
          0%{transform:translate(-50%,-50%) scale(1);opacity:.6}
          70%{opacity:.15}
          100%{transform:translate(-50%,-50%) scale(2.2);opacity:0}
        }
      `}</style>

      <div className="relative z-10 flex items-center gap-3 min-w-0 pr-16">
        <div className="relative shrink-0" style={{width:28,height:28}}>
          <div className="absolute inset-[4px] rounded-full overflow-hidden">
            {pfpSrc ? (
              <img src={pfpSrc} alt="" className="w-5 h-5 rounded-full object-cover mx-auto my-auto absolute inset-0" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-neutral-200 mx-auto my-auto absolute inset-0" />
            )}
          </div>
          <MedalRing activeColors={colors} />
          <TinyStars colors={colors} />
        </div>

        <div className="flex flex-col min-w-0">
          <div className="text-base font-mono truncate max-w-[56vw]">{a.username}</div>
          <div className="text-sm text-neutral-700">ppr × {a.paperSquaresUsed}</div>
          <div className={`inline-flex items-center gap-1 rounded-full px-2 py-[2px] mt-1 text-[10px] ${status.chip}`}>
            <span className="relative inline-block w-3.5 h-3.5">
              {ongoing ? (
                <span
                  className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full bg-green-500 opacity-60"
                  style={{ animation: "pulse2s 3s ease-out infinite" }}
                />
              ) : null}
              <span className={`absolute left-1/2 top-1/2 w-2 h-2 rounded-full ${status.dot} z-10`} style={{ transform: "translate(-50%,-50%)" }} />
            </span>
            {status.text}
          </div>
        </div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className="text-sm font-mono text-neutral-900">{dateStr}</div>
          <div className="text-[11px] text-neutral-500">
            {endTime ? `${startTime} — ${endTime}` : startTime}
          </div>
        </div>
      </div>

      {a.pokemonSpriteUrl ? (
        <img src={a.pokemonSpriteUrl} alt="pokemon" className="absolute top-2 right-2 w-9 h-9 z-30" />
      ) : null}
    </div>
  );
}

function overlapSeconds(aStart, aEnd, dayStart, dayEnd) {
  const s = Math.max(aStart.getTime(), dayStart.getTime());
  const e = Math.min(aEnd.getTime(), dayEnd.getTime());
  return Math.max(0, Math.floor((e - s) / 1000));
}

function computeTodayStats(list) {
  const now = new Date();
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(now);
  dayEnd.setHours(23, 59, 59, 999);
  let activities = 0;
  let seconds = 0;
  for (const a of list) {
    const s = new Date(a.startedAt);
    const e = a.endedAt ? new Date(a.endedAt) : new Date();
    const hasOverlap = e > dayStart && s < dayEnd;
    if (!hasOverlap) continue;
    activities += 1;
    seconds += overlapSeconds(s, e, dayStart, dayEnd);
  }
  return { activities, seconds };
}

// Obvious sliding switch between the dashboard and the map view.
function ViewToggle({ navigate }) {
  return (
    <div
      role="tablist"
      aria-label="vista"
      className="relative inline-flex shrink-0 items-center select-none rounded-full border border-neutral-300 bg-neutral-100 p-1 font-mono text-sm shadow-inner whitespace-nowrap"
    >
      <span
        className="pointer-events-none absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-black shadow transition-transform duration-200 ease-out"
        aria-hidden="true"
      />
      <button
        type="button"
        role="tab"
        className="relative z-10 rounded-full px-4 py-1.5 text-white whitespace-nowrap"
        aria-selected="true"
      >
        Panel
      </button>
      <button
        type="button"
        role="tab"
        className="relative z-10 rounded-full px-4 py-1.5 text-neutral-500 whitespace-nowrap"
        onClick={() => navigate("/map")}
        aria-selected="false"
      >
        Mapa ›
      </button>
    </div>
  );
}

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [today, setToday] = useState({ activities: 0, seconds: 0 });
  const [me, setMe] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("fw_token") || "";

  const load = useCallback(async () => {
    const tok = localStorage.getItem("fw_token");
    if (!tok) return;
    setLoading(true);
    try {
      const meResp = await getMe().catch(() => null);
      setMe(meResp);
      const [list50, st] = await Promise.all([recent(50), getStats()]);
      setItems(list50.slice(0, 5));
      setStats(st);
      setToday(computeTodayStats(list50));
    } catch (err) {
      if (err.message?.includes("401") || err.message?.includes("403")) {
        localStorage.removeItem("fw_token");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    const tryLoad = async () => {
      if (cancelled) return;
      const hasToken = !!localStorage.getItem("fw_token");
      if (hasToken) {
        await load();
      } else {
        setTimeout(tryLoad, 200);
      }
    };
    tryLoad();
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const username = me?.username || localStorage.getItem("fw_username");
  const hasOngoingMine = items.some((a) => a.ongoing && a.username === username);

  const onStartOrResume = useCallback(async () => {
    if (!hasOngoingMine) {
      try { await startActivity(); } catch {}
    }
    navigate("/activity");
  }, [hasOngoingMine, navigate]);

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <div className="px-4 pt-6 pb-3 flex items-center justify-between gap-2">
        <ViewToggle navigate={navigate} />
        <div className="flex flex-wrap justify-end gap-1.5 min-w-0">
          <button className="rounded-full border px-2.5 py-1.5 text-xs whitespace-nowrap" onClick={() => navigate("/comer")}>
            Comer
          </button>
          <button className="rounded-full border px-2.5 py-1.5 text-xs whitespace-nowrap" onClick={() => navigate("/pokedex")}>
            Pokédex
          </button>
          <button className="rounded-full border px-2.5 py-1.5 text-xs whitespace-nowrap" onClick={() => navigate("/profile")}>
            Perfil
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6">
        {loading && items.length === 0 ? (
          <div className="text-neutral-500">Cargando…</div>
        ) : items.length > 0 ? (
          <div className="grid grid-rows-5 gap-1.5 h-full">
            {items.map((a, i) => (
              <div key={i} className="min-h-0">
                <Item a={a} token={token} stats={stats} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-neutral-500 h-full">Sin actividades aún.</div>
        )}
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur px-6 border-t border-neutral-200" style={{height:"16.6667vh"}}>
        <div className="h-full flex flex-col items-center justify-center gap-3">
          <div className="text-[11px] text-neutral-600 text-center">
            hoy: {today.activities} actividades · {fmtHMS(today.seconds)}
          </div>
          <div className="flex gap-2 w-full justify-center" style={{maxWidth: 360}}>
            <button
              className="rounded-full px-4 py-4 text-sm border border-neutral-200 bg-white text-black"
              onClick={() => navigate("/casino")}
              style={{flex: "1 1 33%"}}
            >
              Casino
            </button>
            <button
              className={`rounded-full px-6 py-4 text-lg ${hasOngoingMine ? "bg-yellow-400 text-black" : "bg-black text-white"}`}
              onClick={onStartOrResume}
              style={{flex: "2 1 67%"}}
            >
              {hasOngoingMine ? "Reanudar" : "Start"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
